import type { ButtonHTMLAttributes, MouseEventHandler, ReactNode } from 'react';

import Icon from 'components/Icon';
import Img from 'components/Img';
import PlainButton from 'components/PlainButton';
import { useId } from 'hooks';
import { cn } from 'utils/classNames';

interface Props extends ButtonHTMLAttributes<HTMLButtonElement> {
	imageUrl?: string;
	label: string;
	onClick: MouseEventHandler<HTMLButtonElement>;
	supportingAction?: ReactNode;
	supportingText?: string;
	text: string;
}

/** A button that looks like a select */
export default function SelectButton({
	className,
	disabled,
	imageUrl,
	label,
	onClick,
	supportingAction,
	supportingText,
	text,
	...attrs
}: Props) {
	const id = useId('select-button');
	const labelId = `${id}-label`;
	const textId = `${id}-text`;
	const supportingTextId = `${id}-supporting-text`;

	return (
		<div className={cn('relative', className)}>
			<div className="flex items-end justify-between gap-x-4">
				<p id={labelId} className="font-bold">
					{label}
					{/* Comma for screen reader pause between label and text, since both are set as the label via aria-labelledby. */}
					<span className="sr-only">, </span>
				</p>
				{supportingAction}
			</div>
			<PlainButton
				{...attrs}
				aria-labelledby={`${labelId} ${textId}`}
				aria-describedby={supportingText ? supportingTextId : undefined}
				onClick={onClick}
				className={cn(
					'relative flex items-center text-left',
					'mt-1 h-14 w-full pr-12 pl-4',
					'rounded-xs border border-strong bg-base',
					!disabled && 'hover:bg-baseHover active:bg-baseActive',
					disabled && 'cursor-default border-subtle text-disabled',
				)}
			>
				{imageUrl && (
					<Img
						src={imageUrl}
						alt=""
						className="mr-2 size-8"
						jpgOptimized={false}
						width={32}
						height={32}
					/>
				)}
				<span
					id={textId}
					className="overflow-hidden text-ellipsis whitespace-nowrap"
				>
					{text}
				</span>
				<span className="pointer-events-none absolute inset-y-2 right-px flex w-10 items-center justify-center border-l border-strong">
					<Icon name="chevron-right" />
				</span>
			</PlainButton>
			{supportingText && (
				<p id={supportingTextId} className="mt-1 text-sm">
					{supportingText}
				</p>
			)}
		</div>
	);
}
SelectButton.displayName = 'SelectButton';
